import { useTranslations } from "next-intl";
import { ofetch } from "ofetch";
import { useCallback, useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useMediaPreview } from "@/components/transcriptions/MediaPreview";

export const useMediaInputValidation = ({
  maxSizeMb,
  maxDurationS,
  urlFieldValue,
  form,
}: {
  maxSizeMb: number;
  maxDurationS: number;
  urlFieldValue?: string;
  // Uses any because this hook is shared across forms with different schemas — only accesses mediaInput.* fields
  form: UseFormReturn<any>;
}) => {
  const t = useTranslations();

  const [urlInfo, setUrlInfo] = useState<UrlInfo>();
  const [urlInfoLoading, setUrlInfoLoading] = useState(false);
  // Single-file size/duration validation. Multi-file path computes per-file
  // metadata in the batch hook (see useBatchUpload) and skips this branch.
  const fileInputs = form.watch("mediaInput.files") as File[] | undefined;
  const firstFile = fileInputs?.[0];
  const { duration, sizeMb } = useMediaPreview({
    file: firstFile,
  });

  const handleUrlInput = useCallback(async () => {
    setUrlInfo(undefined);
    form.clearErrors("mediaInput.youtubeUrl");
    if (!urlFieldValue) {
      return;
    }
    let url: URL;
    try {
      url = new URL(urlFieldValue);
    } catch (e) {
      form.setError("mediaInput.youtubeUrl", {
        type: "manual",
        message: t("uploadForm.invalidUrlMessage"),
      });
      return;
    }
    // Should use python backend with yt_dlp for this
    //return;
    setUrlInfoLoading(true);
    try {
      const urlInfo = await ofetch<{ type: string }>("/api/v1/file-url", {
        query: { url: urlFieldValue },
      });
      if (!urlInfo.type) {
        form.setError("mediaInput.youtubeUrl", {
          type: "manual",
          message: t("uploadForm.invalidUrlFileMessage"),
        });
      } else {
        form.clearErrors("mediaInput.youtubeUrl");
        setUrlInfo(urlInfo);
      }
    } catch (e) {
      form.setError("mediaInput.youtubeUrl", {
        type: "manual",
        message: t("uploadForm.invalidUrlFileMessage"),
      });
    } finally {
      setUrlInfoLoading(false);
    }
  }, [urlFieldValue]);
  useEffect(() => {
    handleUrlInput();
  }, [urlFieldValue]);

  useEffect(() => {
    if (sizeMb > maxSizeMb) {
      //setFormMessage(t("uploadForm.tooLargeFileMessage", { size: "2000MB" }));
      if (!form.getFieldState("mediaInput.files")?.error) {
        form.setError("mediaInput.files", {
          type: "manual",
          message: t("uploadForm.tooLargeFileMessage", {
            size: `${maxSizeMb}MB`,
          }),
        });
      }
    }
    if (duration > maxDurationS) {
      //setFormMessage(t("uploadForm.tooLongMediaMessage", { duration: "4" }));
      if (!form.getFieldState("mediaInput.files")?.error) {
        form.setError("mediaInput.files", {
          type: "manual",
          message: t("uploadForm.tooLongMediaMessage", {
            duration: `${Math.floor(maxDurationS / 3600)}`,
          }),
        });
      }
    }
  }, [sizeMb, duration]);

  useEffect(() => {
    if (urlInfo?.duration_s != null && urlInfo.duration_s > maxDurationS) {
      //setFormMessage(t("uploadForm.tooLongMediaMessage", { duration: "4" }));
      if (!form.getFieldState("mediaInput.youtubeUrl")?.error) {
        form.setError("mediaInput.youtubeUrl", {
          type: "manual",
          message: t("uploadForm.tooLongMediaMessage", {
            duration: `${Math.floor(maxDurationS / 3600)}`,
          }),
        });
      }
    }
  }, [urlInfo]);

  return {
    urlInfo,
    urlInfoLoading,
    setUrlInfo,
  };
};
